import React from 'react'
import { useAuth } from '../../hooks/useAuth'

interface UserAvatarProps {
  compact?: boolean
}

export const UserAvatar: React.FC<UserAvatarProps> = ({ compact = false }) => {
  const { user, logout } = useAuth()
  const [open, setOpen] = React.useState(false)

  if (!user) return null

  const initials = user.name
    ? user.name
        .split(' ')
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase()
    : '?'

  return (
    <>
      <style>{`
        .user-avatar-wrap {
          position: relative;
        }
        .user-avatar-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(133, 183, 235, 0.1);
          border: 0.5px solid rgba(133, 183, 235, 0.3);
          color: var(--accent-blue);
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.73rem;
          padding: ${compact ? '2px 8px' : '4px 10px'};
          border-radius: var(--radius-sm, 4px);
          cursor: pointer;
          transition: all 0.15s ease;
          white-space: nowrap;
        }
        .user-avatar-btn:hover {
          background: rgba(133, 183, 235, 0.18);
          border-color: rgba(133, 183, 235, 0.5);
        }
        .user-avatar-initials {
          background: var(--accent-blue);
          color: #1a1a18;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.62rem;
          font-weight: 700;
          flex-shrink: 0;
        }
        .user-dropdown {
          position: absolute;
          top: calc(100% + 6px);
          right: 0;
          background-color: var(--bg-elevated);
          border: 0.5px solid var(--border-color-solid);
          border-radius: var(--radius-md, 8px);
          padding: 10px 0;
          min-width: 160px;
          z-index: 200;
          box-shadow: 0 4px 16px rgba(0,0,0,0.35);
        }
        .user-dropdown-name {
          padding: 4px 14px 10px;
          font-size: 0.78rem;
          color: var(--text-secondary);
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          border-bottom: 0.5px solid var(--border-color-solid);
          margin-bottom: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .user-dropdown-item {
          display: block;
          padding: 7px 14px;
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.78rem;
          color: var(--text-secondary);
          cursor: pointer;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          transition: color 0.15s ease, background-color 0.15s ease;
        }
        .user-dropdown-item:hover {
          color: var(--accent-red);
          background-color: rgba(248, 113, 113, 0.06);
        }
      `}</style>
      <div className="user-avatar-wrap">
        <button
          className="user-avatar-btn"
          onClick={() => setOpen((o) => !o)}
          aria-label={`User menu for ${user.name}`}
          aria-expanded={open}
        >
          <span className="user-avatar-initials">{initials}</span>
          {!compact && <span>{user.name.split(' ')[0]}</span>}
        </button>

        {open && (
          <>
            {/* Backdrop */}
            <div
              style={{
                position: 'fixed', inset: 0, zIndex: 199,
              }}
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
            <div className="user-dropdown" role="menu">
              <div className="user-dropdown-name">{user.email}</div>
              <button
                className="user-dropdown-item"
                role="menuitem"
                onClick={() => { logout(); setOpen(false) }}
              >
                [sign out]
              </button>
            </div>
          </>
        )}
      </div>
    </>
  )
}
